// hooks/usePlanTracker.js
import { useState, useEffect, useRef } from 'react';
import {
  doc, getDoc, updateDoc, setDoc, onSnapshot, increment,
  collection, addDoc, deleteDoc, query, orderBy, limit, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export const PLANS = {
  PAID:     { filingsAllowed: 1,        isUnlimited: false },
  LIFETIME: { filingsAllowed: Infinity, isUnlimited: true  },
};

// Status lifecycle:
//   DRAFT     → PDF generated, never sent
//   NOT_FAXED → edited/regenerated after a previous send
//   FAXED     → faxId received from queue, delivery in progress  ← NEW MEANING
//   DELIVERED → polling confirmed IRS receipt                    ← NEW STATUS
export const FILING_STATUS = {
  DRAFT:     'draft',
  NOT_FAXED: 'not_faxed',
  FAXED:     'faxed',
  DELIVERED: 'delivered',
  // NEW: terminal status for single-shot tools that don't go through a
  // fax/delivery lifecycle — ITIN (package generated + emailed, nothing to
  // poll for). Annual Report used to land here too, but it's now fully
  // client-side (components/AnnualReport.js) and never writes a
  // filing_history record — free for everyone, nothing to track. Old
  // 'annualReport' records from before that change may still exist and
  // will still show FILED if someone clicks into them from the sidebar.
  FILED:     'filed',
};

// Maps a tool's `type` to the human label used when building sidebar titles.
// 'annualReport' is kept only so old pre-existing history records (from
// before Annual Report went fully client-side) still render a sane title.
export const TOOL_LABELS = {
  '5472':       'Form 5472',
  ein:          'EIN Application',
  itin:         'ITIN Package',
  annualReport: 'Annual Report',
};

export const usePlanTracker = (userId) => {
  const [currentPlan,    setCurrentPlan   ] = useState(null);
  const [filingsUsed,    setFilingsUsed   ] = useState(0);
  const [filingsAllowed, setFilingsAllowed] = useState(0);
  const [freeFilingUsed, setFreeFilingUsed] = useState(false); // ← NEW
  const [history,        setHistory       ] = useState([]);
  const [loading,        setLoading       ] = useState(true);
  const isConsuming = useRef(false);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    let unsubHistory = null;

    const init = async () => {
      const [userSnap, usageSnap] = await Promise.all([
        getDoc(doc(db, 'users', userId)),
        getDoc(doc(db, 'usage', userId)),
      ]);

      const raw = userSnap.data()?.plan?.replace(/"/g, '').trim().toUpperCase() || null;
      setCurrentPlan(raw);
      setFilingsUsed(usageSnap.data()?.filingsUsed || 0);
      setFilingsAllowed(usageSnap.data()?.filingsAllowed || 0);
      setFreeFilingUsed(!!usageSnap.data()?.freeFilingUsed); // ← NEW

      const q = query(
        collection(db, 'users', userId, 'filing_history'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      unsubHistory = onSnapshot(q, (snap) => {
        setHistory(
          snap.docs.map(d => ({
            id:        d.id,
            ...d.data(),
            timestamp: d.data().timestamp?.toDate() || new Date(),
            updatedAt: d.data().updatedAt?.toDate()  || null,
          }))
        );
        setLoading(false);
      }, (err) => {
        console.error('[usePlanTracker] history snapshot error:', err);
        setLoading(false);
      });
    };

    init().catch(err => {
      console.error('[usePlanTracker] init error:', err);
      setLoading(false);
    });

    return () => { if (unsubHistory) unsubHistory(); };
  }, [userId]);

  const hasPurchased    = () => currentPlan === 'PAID' || currentPlan === 'LIFETIME';
  const isUnlimitedPlan = () => currentPlan === 'LIFETIME';

  // ← NEW: no plan yet, and hasn't burned the free one
  const hasFreeFilingLeft = () => !hasPurchased() && !freeFilingUsed;

  const canGenerateQR = () => {
    if (isConsuming.current) return false;
    if (hasFreeFilingLeft()) return true;       // ← NEW: let free users through
    if (!hasPurchased())     return false;
    if (isUnlimitedPlan())   return true;
    return filingsUsed < filingsAllowed;
  };

  const getLimitReason = () => {
    if (hasFreeFilingLeft()) return null;        // ← NEW
    if (!hasPurchased()) return 'Purchase a plan to start filing.';
    if (isUnlimitedPlan()) return null;
    if (filingsUsed >= filingsAllowed) return 'You have used all your filing credits. Purchase another to continue.';
    return null;
  };

  const consumeCredit = async () => {
    if (!canGenerateQR()) return false;
    isConsuming.current = true;
    try {
      // ← NEW: if this run is the free one, burn that instead of a paid credit
      if (hasFreeFilingLeft()) {
        await setDoc(doc(db, 'usage', userId), { freeFilingUsed: true }, { merge: true });
        setFreeFilingUsed(true);
        return true;
      }

      await updateDoc(doc(db, 'usage', userId), { filingsUsed: increment(1) });
      setFilingsUsed(n => n + 1);
      return true;
    } catch (e) {
      console.error('consumeCredit error:', e);
      return false;
    } finally {
      isConsuming.current = false;
    }
  };

  // ── Step 1: called right after PDF is first generated ────────────────────
  const createDraft = async (title, filingData, transactions, pdfBase64) => {
    if (!userId) return null;
    try {
      const ref = await addDoc(
        collection(db, 'users', userId, 'filing_history'),
        {
          title,
          status:          FILING_STATUS.DRAFT,
          formData:        normaliseFormData(filingData),
          transactions:    transactions    || [],
          pdfBase64:       pdfBase64       || null,
          faxId:           null,           // populated by markFaxed()
          faxConfirmation: null,           // populated by markDelivered()
          timestamp:       serverTimestamp(),
          updatedAt:       serverTimestamp(),
        }
      );
      return ref.id;
    } catch (e) {
      console.error('createDraft error:', e);
      return null;
    }
  };

  // ── Step 2: called whenever user edits and regenerates the PDF ───────────
  const updateDraft = async (id, filingData, transactions, pdfBase64) => {
    if (!userId || !id) return false;
    try {
      await updateDoc(doc(db, 'users', userId, 'filing_history', id), {
        formData:     normaliseFormData(filingData),
        transactions: transactions || [],
        pdfBase64:    pdfBase64    || null,
        status:       FILING_STATUS.NOT_FAXED,
        updatedAt:    serverTimestamp(),
      });
      return true;
    } catch (e) {
      console.error('updateDraft error:', e);
      return false;
    }
  };

  // ── Step 3a: called THE MOMENT faxId comes back from the queue ───────────
  // Sets status → FAXED and stores faxId at the top level so the sidebar
  // can display it without parsing faxConfirmation. This fires immediately,
  // before polling has confirmed delivery.
  const markFaxed = async (id, faxId) => {
    if (!userId || !id) return false;
    try {
      await updateDoc(doc(db, 'users', userId, 'filing_history', id), {
        status:    FILING_STATUS.FAXED,
        faxId:     faxId || null,
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (e) {
      console.error('markFaxed error:', e);
      return false;
    }
  };

  // ── Step 3b: called when polling confirms IRS delivery ───────────────────
  // Upgrades status → DELIVERED and stores the full confirmation object.
  // faxId is preserved from Step 3a (not overwritten).
  const markDelivered = async (id, faxConfirmation) => {
    if (!userId || !id) return false;
    try {
      await updateDoc(doc(db, 'users', userId, 'filing_history', id), {
        status:          FILING_STATUS.DELIVERED,
        faxConfirmation: faxConfirmation || null,
        updatedAt:       serverTimestamp(),
      });
      return true;
    } catch (e) {
      console.error('markDelivered error:', e);
      return false;
    }
  };

  // ── Generic single-shot history writer for EIN / ITIN ────────────────────
  // Unlike the 5472 flow (createDraft → updateDraft → markFaxed → markDelivered),
  // these tools complete in one round trip and already have their final
  // confirmation by the time this is called, so we just write the finished
  // record straight away. `type` ('ein' | 'itin') is what lets the
  // sidebar and FilingGenerator route a clicked history item back to the
  // right tool tab. Annual Report used to write records through here too
  // (type: 'annualReport') but is now fully client-side and never calls this
  // — old records with that type may still exist and will still load fine.
  const recordToolFiling = async ({ type, title, formData, pdfBase64, confirmation, status }) => {
    if (!userId) return null;
    try {
      const ref = await addDoc(
        collection(db, 'users', userId, 'filing_history'),
        {
          type:            type || '5472',
          title:           title || (TOOL_LABELS[type] || 'Filing'),
          status:          status || FILING_STATUS.FILED,
          formData:        formData || {},
          transactions:    [],
          pdfBase64:       pdfBase64    || null,
          faxId:           confirmation?.faxId || confirmation?.submissionId || null,
          faxConfirmation: confirmation || null,
          timestamp:       serverTimestamp(),
          updatedAt:       serverTimestamp(),
        }
      );
      return ref.id;
    } catch (e) {
      console.error('recordToolFiling error:', e);
      return null;
    }
  };

  const deleteFiling = async (id) => {
    if (!userId) return false;
    try {
      await deleteDoc(doc(db, 'users', userId, 'filing_history', id));
      return true;
    } catch (e) {
      console.error('deleteFiling error:', e);
      return false;
    }
  };

  const getUsageStats = () => ({
    hasPurchased: hasPurchased(),
    plan:         currentPlan,
    filingsUsed,
    filingsAllowed:   isUnlimitedPlan() ? 'Unlimited' : filingsAllowed,
    filingsRemaining: isUnlimitedPlan() ? 'Unlimited' : Math.max(0, filingsAllowed - filingsUsed),
    canGenerate:  canGenerateQR(),
    usingFreeFiling: hasFreeFilingLeft(), // ← NEW
  });

  const getRelativeTime = (date) => {
    if (!date) return '';
    const mins  = Math.floor((new Date() - date) / 60000);
    const hours = Math.floor(mins  / 60);
    const days  = Math.floor(hours / 24);
    if (mins  < 1)   return 'Just now';
    if (mins  < 60)  return `${mins}m ago`;
    if (hours < 24)  return `${hours}h ago`;
    if (days  === 1) return 'Yesterday';
    if (days  < 7)   return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return {
    loading,
    currentPlan,
    hasPurchased,
    hasFreeFilingLeft, // ← NEW
    canGenerateQR,
    getLimitReason,
    consumeCredit,
    getUsageStats,
    history,
    createDraft,
    updateDraft,
    markFaxed,
    markDelivered,
    recordToolFiling,
    deleteFiling,
    getRelativeTime,
  };
};

function normaliseFormData(f = {}) {
  return {
    llcName:          f.llcName          || '',
    ein:              f.ein              || '',
    taxYear:          f.taxYear          || '',
    ownerName:        f.ownerName        || '',
    ownerEmail:       f.ownerEmail       || '',
    ownerCountry:     f.ownerCountry     || '',
    ownerTitle:       f.ownerTitle       || '',
    ownerAddress:     f.ownerAddress     || '',
    city:             f.city             || '',
    stateCode:        f.stateCode        || '',
    country:          f.country          || '',
    businessActivity: f.businessActivity || '',
    businessCode:     f.businessCode     || '',
    isFirstYear:      f.isFirstYear      || false,
  };
}